import { useState, useEffect } from "react";
import { servicesApi, type ServiceContent } from "@/integrations/mysql/client";
import { useToast } from "@/hooks/use-toast";

export type { ServiceContent };

export function useServicesContent(section?: string) {
  const [content, setContent] = useState<ServiceContent[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast }             = useToast();

  const fetchContent = async () => {
    try {
      setLoading(true);
      const data = await servicesApi.list(section);
      setContent(Array.isArray(data) ? data : []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Erro desconhecido";
      toast({ variant: "destructive", title: "Erro ao carregar conteúdo", description: msg });
    } finally {
      setLoading(false);
    }
  };

  const updateContent = async (id: string, updates: Partial<ServiceContent>) => {
    try {
      await servicesApi.update(id, updates);
      toast({ title: "Conteúdo atualizado", description: "Alterações salvas com sucesso." });
      await fetchContent();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Erro desconhecido";
      toast({ variant: "destructive", title: "Erro ao atualizar", description: msg });
    }
  };

  const createContent = async (newContent: Omit<ServiceContent, 'id'>) => {
    try {
      await servicesApi.create(newContent);
      toast({ title: "Conteúdo criado", description: "Item adicionado com sucesso." });
      await fetchContent();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Erro desconhecido";
      toast({ variant: "destructive", title: "Erro ao criar", description: msg });
    }
  };

  const deleteContent = async (id: string) => {
    try {
      await servicesApi.delete(id);
      toast({ title: "Conteúdo removido", description: "Item deletado com sucesso." });
      await fetchContent();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Erro desconhecido";
      toast({ variant: "destructive", title: "Erro ao deletar", description: msg });
    }
  };

  useEffect(() => { fetchContent(); }, [section]);

  return { content, loading, updateContent, createContent, deleteContent, refetch: fetchContent };
}
